import type { OpenerGroup } from '../../api/useGroups';
import { formatEventStart } from './entrySort';

/**
 * What the group picker shows about a group's schedule: how many of its pairs
 * are already under way, and when the next one starts.
 *
 * Both numbers come from the group list itself (`liveCount` /
 * `nextEventStartAt`, computed by the opener-orchestrator over the same
 * opener-eligible pair universe the `pairCount` uses) — the picker never loads
 * a group's rules, which is the whole point of the landing screen.
 */
export interface GroupSchedule {
  /** Pairs whose event already started. 0 → no LIVE chip. */
  liveCount: number;
  /** Buenos Aires label for the next start, or null when there is none. */
  nextStartLabel: string | null;
}

export function groupSchedule(
  group: Pick<OpenerGroup, 'liveCount' | 'nextEventStartAt'>,
  nowMs: number
): GroupSchedule {
  const liveCount = group.liveCount ?? 0;
  const iso = group.nextEventStartAt;
  if (!iso) return { liveCount, nextStartLabel: null };

  const startMs = new Date(iso).getTime();
  // An unparseable date must not render as `NaN:NaN`, and a start that has
  // already passed must not be announced as upcoming: the list refetches every
  // 30s, so a group's next event can begin between two fetches. Dropping the
  // label leaves the row saying nothing rather than something false.
  if (!Number.isFinite(startMs) || startMs <= nowMs) {
    return { liveCount, nextStartLabel: null };
  }
  return { liveCount, nextStartLabel: formatEventStart(startMs, nowMs) };
}
